/**
 * WHICH PROTOCOL THE SERVER IS ACTUALLY SERVING — the one owner of that fact on the client.
 *
 * ── WHAT THIS REPLACES, AND WHY IT IS NOT A FLAG ────────────────────────────────────────────
 *
 * `protocol/flag.ts` used to answer "is v1 on" from the CLIENT's side: a route, a localStorage
 * override, a build-time env var — and, after `/weave/v1` was deleted, from a hard-coded
 * `return true`. Two things then owned one fact. The client asserted v1; the server decided it
 * from `MSA_GUI_PROTOCOL_V1`, which is FALSE in production. A server with the protocol off does
 * not refuse `?protocol=1` — it silently serves the legacy flat frames and 404s
 * `GET /api/runs/{id}/state`. The v1 reducer returns `{state: null}` until a `state.snapshot` that
 * is never coming, every `runStateStore` selector then returns its empty default, and the page
 * renders an empty transcript with no error anywhere. That is the P0 this module exists to close.
 *
 * So the fact has exactly one owner now, and it is THE SERVER. The client asks for v1 and then
 * believes what it is told; nothing here consults the environment, storage, or the URL, and
 * nothing should be added that does. A client-side opinion about the server's capabilities is the
 * defect, not the fix.
 *
 * ── THE FOUR ADVERTISEMENTS (MSA leg C-57) ──────────────────────────────────────────────────
 *
 *   a. `POST /api/runs` → `protocol_version: "1" | "legacy"` in the JSON body. The earliest and
 *      most direct signal, and the only one available BEFORE a stream is opened. Absent on an old
 *      server and on any run this client did not create (a run restored from history).
 *   b. The SSE response header `X-Weave-Protocol: 1|legacy`, present on every request. Read in
 *      `sse.ts` at the moment the response arrives, before a single frame is parsed.
 *   c. The first SSE event when a `?protocol=1` request is served legacy: a legacy-shaped
 *      `protocol_downgraded` carrying `{requested, served, reason}`. Belt and braces for a proxy
 *      that strips response headers.
 *   d. `GET /api/runs/{id}/state` answering `404 {"detail":"protocol_unavailable"}`. The last line:
 *      it fires even if a run was created elsewhere AND the headers were stripped.
 *
 * ── HOW THEY RESOLVE ────────────────────────────────────────────────────────────────────────
 *
 * Two rules, in this order:
 *
 *   1. ANY OBSERVED `legacy` WINS, whichever source said it. `legacy` is only ever reported by a
 *      server that has just told us what it really served; `"1"` can be a stale or optimistic
 *      advertisement that a later observation contradicts. A "first source wins" precedence would
 *      let a run-create saying `"1"` outvote a stream header saying `legacy` — i.e. it would
 *      reproduce the exact blank page this module exists to prevent. A downgrade is therefore
 *      STICKY for the life of the run.
 *   2. Otherwise the first source with an opinion, in the order a → b → c → d. Absent a `legacy`
 *      that is always `"1"`; the ordering is what decides which source is quoted, and it is the
 *      order the signals arrive in.
 *
 * DEFAULT WHEN NOTHING IS ADVERTISED: `"1"`. An OLD SERVER — one predating C-57 — sends no
 * `protocol_version`, no header, and no downgrade event, and the client must keep behaving exactly
 * as it does today (v1), because such a server is one that already serves v1 correctly. The
 * belt-and-braces guard for the remaining case (an old server that is ALSO protocol-off, which can
 * advertise nothing at all) is the 10-second empty-state notice in `useProtocolMismatchNotice`,
 * not a default flipped to legacy.
 *
 * ── KNOWN LIMITS, WRITTEN DOWN RATHER THAN DISCOVERED ───────────────────────────────────────
 *
 *   · THE SESSION LATCH. `lastServed` gives a run nobody has heard about the session's most recent
 *     answer, so a run RESTORED FROM HISTORY after any legacy observation starts on the legacy
 *     path. It then gets no fresh advertisement to correct it: `sse.ts` deliberately does not
 *     record the response header on a legacy subscribe (recording it would let the legacy path
 *     ratify its own assumption), and the v1-only `GET /state` is never called. So on a server
 *     that is serving v1, one legacy observation earlier in the session latches every later
 *     restored run to the legacy transcript until the tab is reloaded.
 *     ACCEPTED, because the failure is the SAFE direction — a working legacy transcript rather
 *     than a blank v1 one — and because the case needs a session that saw both answers, which in
 *     practice means a deploy flipping `MSA_GUI_PROTOCOL_V1` mid-session. The fix, when it is
 *     worth it, is for the legacy subscribe to record an explicit `1` (and only a `1`) from the
 *     header, which re-upgrades without letting a legacy echo confirm itself.
 *
 *   · THE RIGHT-HAND COLUMN IS BLANK ON LEGACY. `MultiAgentRoute` passes `workspace={<WeaveWorkspace/>}`
 *     unconditionally, and `WeaveWorkspace` reads `runStateStore`, which is empty on the legacy
 *     path. The legacy column it replaced (`MultiAgentRightPanels`, Run details · Impact · Tests ·
 *     Dependencies) was deleted in 4a1d765f and is not restored here — the edge falls through to
 *     the code editor. A chrome gap, not the blank-page P0: the conversation, the answer, the
 *     artifacts and the clarification card all render.
 *
 * Per repo convention every export below that carries logic is PURE and takes its inputs as
 * parameters, so the rules are testable in the node (no-DOM) vitest environment; the store at the
 * bottom is the only stateful part and it is a plain record keyed by run id.
 */

import { create } from "zustand";
import { registerOrgScopedReset } from "@/lib/org-scope";

/** What the server says it is serving for a given run. */
export type ServedProtocol = "1" | "legacy";

/** The negotiation header, sent on the v1 request and returned on every response. */
export const PROTOCOL_HEADER = "X-Weave-Protocol";

/** The legacy-shaped frame a protocol-off server sends first when `?protocol=1` was requested. */
export const PROTOCOL_DOWNGRADED_EVENT = "protocol_downgraded";

/** `GET /state` (and `/session`, `/interactions*`) 404 body when the protocol is off. */
export const PROTOCOL_UNAVAILABLE_DETAIL = "protocol_unavailable";

/**
 * Nothing advertised — an old server, which serves v1. See the header for why this is not `legacy`.
 */
export const DEFAULT_SERVED_PROTOCOL: ServedProtocol = "1";

/** The advertisement sources, named so a resolution can say which one it came from. */
export type ProtocolSource = "runCreate" | "streamHeader" | "downgradeEvent" | "stateProbe";

/** Precedence order for rule 2. Exported so the test asserts the order rather than restating it. */
export const SOURCE_ORDER: readonly ProtocolSource[] = [
  "runCreate",
  "streamHeader",
  "downgradeEvent",
  "stateProbe",
];

/** What each source said, or nothing when it has not spoken. */
export type ProtocolAdvertisements = Partial<Record<ProtocolSource, ServedProtocol>>;

/**
 * Parse one advertised value.
 *
 * Returns `null` for "no opinion" so an absent header or an unrecognised string falls through to
 * the next source rather than reading as a downgrade — a proxy that rewrites the header to
 * something we do not know about must not blank the page any more than a missing one does.
 *
 * `"0"`/`"false"`/`"off"` are accepted as `legacy` because the server's flag is a boolean and an
 * intermediary serialising it as one is likelier than it being wrong on purpose.
 */
export function parseServedProtocol(raw: unknown): ServedProtocol | null {
  if (raw === 1) return "1";
  if (raw === 0 || raw === false) return "legacy";
  if (raw === true) return "1";
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase();
  if (value === "1" || value === "v1" || value === "true" || value === "on") return "1";
  if (value === "legacy" || value === "0" || value === "false" || value === "off") return "legacy";
  return null;
}

function isObj(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Source (c): is this the downgrade frame, and what does it say was served?
 *
 * DELIBERATELY TOLERANT ABOUT WHERE THE NAME IS. A legacy flat frame carries its type in the JSON
 * (`event_type`, or `event` on the older emitters `sse.ts` already reads both of), but SSE also
 * carries a name in the `event:` FIELD, which `sse.ts` does not otherwise parse — and the contract
 * describes this frame as "a legacy-shaped `protocol_downgraded`" without saying which. Reading
 * both costs four lines in the frame loop; reading one and guessing wrong costs the whole fix,
 * silently, because a missed downgrade looks exactly like the bug.
 *
 * The `{requested, served}` shape is accepted on its own as a third spelling for the same reason.
 *
 * `served` is honoured when present (a future frame could announce something other than legacy);
 * a downgrade frame that names nothing means `legacy`, since that is the only reason to send one.
 */
export function readDowngradeFrame(
  eventName: string | null | undefined,
  payload: unknown,
): ServedProtocol | null {
  const body = isObj(payload) ? payload : null;
  const named =
    eventName === PROTOCOL_DOWNGRADED_EVENT ||
    body?.event_type === PROTOCOL_DOWNGRADED_EVENT ||
    body?.event === PROTOCOL_DOWNGRADED_EVENT;
  // The payload may be the frame itself, or nested under `data` as the v1 envelope nests it.
  const data = body && isObj(body.data) ? body.data : body;
  const shaped = !!data && "served" in data && "requested" in data;
  if (!named && !shaped) return null;
  return parseServedProtocol(data?.served) ?? "legacy";
}

/**
 * Source (d): does this `GET /state` failure mean the protocol is off, rather than "no such run"?
 *
 * The distinction matters to the caller as much as to this module: a 404 with this detail is a
 * SERVER CONFIGURATION answer about every run, and reporting it as a missing run sends the reader
 * to look for a conversation that is in fact right there.
 */
export function readProtocolUnavailable(status: number, body: unknown): ServedProtocol | null {
  if (status !== 404) return null;
  if (!isObj(body)) return null;
  if (body.detail !== PROTOCOL_UNAVAILABLE_DETAIL) return null;
  return parseServedProtocol(body.protocol_version) ?? "legacy";
}

/**
 * A v1-only endpoint answering "this server does not speak v1".
 *
 * ITS OWN TYPE, not a 404 message, because the two 404s mean opposite things and only one of them
 * is about the run the reader asked for. Callers distinguish them with `isProtocolUnavailable`
 * rather than by matching on text.
 */
export class ProtocolUnavailableError extends Error {
  readonly runId: string;
  readonly servedProtocol: ServedProtocol = "legacy";
  constructor(runId: string) {
    super(`protocol_unavailable: the server is serving legacy for run ${runId}`);
    this.name = "ProtocolUnavailableError";
    this.runId = runId;
  }
}

/** Is this the "server does not speak v1" failure, rather than a missing run? */
export function isProtocolUnavailable(err: unknown): err is ProtocolUnavailableError {
  return err instanceof ProtocolUnavailableError;
}

/**
 * The two rules in the header, applied. `null` when no source has an opinion yet.
 *
 * PURE, and the whole decision. Everything else in this module either feeds it an observation or
 * reads its answer.
 */
export function resolveServedProtocol(ads: ProtocolAdvertisements): ServedProtocol | null {
  // Rule 1 — a downgrade, from any source, is the last word.
  for (const source of SOURCE_ORDER) {
    if (ads[source] === "legacy") return "legacy";
  }
  // Rule 2 — otherwise the first source with an opinion, in declared order.
  for (const source of SOURCE_ORDER) {
    const value = ads[source];
    if (value) return value;
  }
  return null;
}

/** `resolveServedProtocol` with the old-server default applied. */
export function servedOrDefault(resolved: ServedProtocol | null): ServedProtocol {
  return resolved ?? DEFAULT_SERVED_PROTOCOL;
}

// ───────────────────────────────────────────────────────────────────────────
// The store — one record of advertisements per run
// ───────────────────────────────────────────────────────────────────────────

/**
 * KEYED BY RUN, not by session or by app.
 *
 * A deploy can flip `MSA_GUI_PROTOCOL_V1` between two runs in the same tab, and a run restored
 * from history was created by whatever the server was at the time. Keying by run means each
 * conversation is served what its own stream says, and means a downgrade cannot leak from a run
 * that failed onto one that would have worked.
 *
 * `lastServed` is the session-wide memory of the most recent answer, used ONLY as the reading for
 * a run that has not been heard from yet — so the first render of a follow-up in a downgraded
 * session does not flash the v1 surface before the stream header lands. It is a head start, never
 * an override: any per-run observation replaces it.
 */
export interface ProtocolNegotiationStore {
  byRun: Readonly<Record<string, ProtocolAdvertisements>>;
  lastServed: ServedProtocol | null;
  observe: (runId: string | null | undefined, source: ProtocolSource, value: unknown) => void;
  reset: () => void;
}

export const useProtocolNegotiationStore = create<ProtocolNegotiationStore>((set) => ({
  byRun: {},
  lastServed: null,
  observe: (runId, source, value) => {
    const served = parseServedProtocol(value);
    if (!runId || !served) return;
    set((state) => {
      const current = state.byRun[runId];
      if (current?.[source] === served) return state; // idempotent — no re-render on a reconnect
      const next = { ...(current ?? {}), [source]: served };
      return {
        byRun: { ...state.byRun, [runId]: next },
        lastServed: resolveServedProtocol(next) ?? state.lastServed,
      };
    });
  },
  reset: () => set({ byRun: {}, lastServed: null }),
}));

/*
 * §6 tenant boundary: what the previous organization's server served says nothing about the next
 * one's, and `runStateStore` and the SSE ticket cache both register the same way.
 */
registerOrgScopedReset("weaveProtocolNegotiation", () =>
  useProtocolNegotiationStore.getState().reset(),
);

/**
 * Record one advertisement. Called from the three places that see one — `api.ts` (run create and
 * the `/state` probe) and `sse.ts` (the stream header and the downgrade frame) — so no component
 * and no hook has to remember to.
 */
export function observeProtocol(
  runId: string | null | undefined,
  source: ProtocolSource,
  value: unknown,
): void {
  useProtocolNegotiationStore.getState().observe(runId, source, value);
}

/** Pure: the served protocol for one run, given the whole store's state. */
export function selectServedProtocol(
  state: Pick<ProtocolNegotiationStore, "byRun" | "lastServed">,
  runId: string | null | undefined,
): ServedProtocol {
  const ads = runId ? state.byRun[runId] : undefined;
  return servedOrDefault(resolveServedProtocol(ads ?? {}) ?? state.lastServed);
}

/** Ambient read, for the handful of non-reactive call sites (`ActionBar`'s self-gate). */
export function servedProtocolFor(runId: string | null | undefined): ServedProtocol {
  return selectServedProtocol(useProtocolNegotiationStore.getState(), runId);
}

/** Reactive read — what components and the route use. */
export function useServedProtocol(runId: string | null | undefined): ServedProtocol {
  return useProtocolNegotiationStore((s) => selectServedProtocol(s, runId));
}
